import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, FileSpreadsheet, FileText, FileImage } from "lucide-react";

interface DocumentViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  fileName: string;
  fileType: string;
}

export function DocumentViewerDialog({
  open,
  onOpenChange,
  url,
  fileName,
  fileType,
}: DocumentViewerDialogProps) {
  const [isLoading, setIsLoading] = useState(true);

  const isImage = ["jpg", "jpeg", "png"].includes(fileType);
  const isPdf = fileType === "pdf";
  const isOfficeDoc = ["doc", "docx", "xls", "xlsx"].includes(fileType);

  const getFileIcon = () => {
    if (isImage) return <FileImage className="h-16 w-16 text-muted-foreground" />;
    if (isPdf) return <FileText className="h-16 w-16 text-muted-foreground" />;
    return <FileSpreadsheet className="h-16 w-16 text-muted-foreground" />;
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderViewer = () => {
    if (isImage) {
      return (
        <div className="flex items-center justify-center min-h-[400px] bg-muted/50 rounded-lg overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-pulse">Loading...</div>
            </div>
          )}
          <img
            src={url}
            alt={fileName}
            className="max-w-full max-h-[70vh] object-contain"
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        </div>
      );
    }

    if (isPdf) {
      return (
        <div className="min-h-[500px] bg-muted/50 rounded-lg overflow-hidden">
          <iframe
            src={`${url}#toolbar=1`}
            className="w-full h-[70vh]"
            title={fileName}
          />
        </div>
      );
    }

    // For Office documents (Word, Excel), show a preview using Office Online viewer
    if (isOfficeDoc) {
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      return (
        <div className="min-h-[500px] bg-muted/50 rounded-lg overflow-hidden">
          <iframe
            src={officeViewerUrl}
            className="w-full h-[70vh]"
            title={fileName}
          />
        </div>
      );
    }

    // Fallback for unsupported types
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-muted/50 rounded-lg p-8 text-center">
        {getFileIcon()}
        <p className="mt-4 text-lg font-medium">{fileName}</p>
        <p className="text-sm text-muted-foreground mt-2">
          Preview not available for this file type. Please download to view.
        </p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between pr-8">
          <DialogTitle className="truncate max-w-[80%]">{fileName}</DialogTitle>
        </DialogHeader>
        <div className="overflow-auto">
          {renderViewer()}
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
          <Button onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
